import * as Dialog from 'primitives/dialog'
import { PaymentStatus } from '@repo/graphql-types'
import { UpgradeCompleteScreenDialog } from '../../components/upgrade-complete-screen-dialog'
import { Z_INDEX_MODALS } from 'constants/modals'
import { getPaymentRecoveryModelFromStatusDialogsResult } from '../../components/payment-recovery-dialog/normalizers'
import { PaymentRecoveryStatusDialog } from '../../components/payment-recovery-dialog/payment-recovery-status-dialog'
import type { StatusDialogsResult as PaymentRecoveryStatusDialogsResult } from '../../components/payment-recovery-dialog/types'

export type StatusDialogsResult = PaymentRecoveryStatusDialogsResult

export interface ActivateTrialStatusDialogsProps {
  onErrorClose: () => void
  onSuccessOpenChange: (open: boolean) => void
  result: StatusDialogsResult | null
  subscriptionType: 'all-access' | 'studio-ai'
  /**
   * Optional z-index for the rendered dialogs. Defaults to
   * `Z_INDEX_MODALS.oneClickUpgrade` (modal `10000`, success `10002`).
   *
   * Override this when the host renders the dialog on top of another modal
   * whose z-index is higher than the default — e.g. the PDP Studio
   * Customizer's `CoinTopUpDialog` sits at `Z_INDEX_MODALS.payment.wp`
   * (`10003`), so the remediation dialog must be at least `10004` to stack
   * on top of it (STU-7698).
   */
  zIndex?: number
  /**
   * Optional portal target. Forwarded to `Dialog.Portal`. Defaults to
   * `document.body`. Used by hosts that scope their UI inside an isolated
   * portal (e.g. the WordPress PDP widget's `data-cf-external` container).
   */
  portalContainer?: HTMLElement | null
}

export function ActivateTrialStatusDialogs({
  onErrorClose,
  onSuccessOpenChange,
  result,
  subscriptionType,
  zIndex,
  portalContainer,
}: ActivateTrialStatusDialogsProps) {
  const recoveryModel = getPaymentRecoveryModelFromStatusDialogsResult(result)

  const errorZIndex = zIndex ?? Z_INDEX_MODALS.oneClickUpgrade.modal
  const successZIndex = zIndex ?? Z_INDEX_MODALS.oneClickUpgrade.success

  return (
    <>
      <PaymentRecoveryStatusDialog
        model={recoveryModel}
        onClose={onErrorClose}
        zIndex={errorZIndex}
        overlayZIndex={errorZIndex - 1}
        portalContainer={portalContainer}
      />

      <Dialog.Root
        open={result?.paymentsStatus === PaymentStatus.Succeeded}
        onOpenChange={onSuccessOpenChange}
        defaultOpen={false}
      >
        <Dialog.Portal container={portalContainer ?? undefined}>
          <div data-cf-external>
            <Dialog.Body overlayZIndex={successZIndex - 1} zIndex={successZIndex}>
              <UpgradeCompleteScreenDialog subscriptionType={subscriptionType} />
            </Dialog.Body>
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
